import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  Switch,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationRules,
  updateNotificationRule,
  deleteNotificationRule,
} from '../services/notificationService';
import NotificationRuleDialog from './dialogs/NotificationRuleDialog';

const RULE_TYPE_LABELS = {
  no_payment: 'לא שילם תוך',
  no_charge: 'לא חויב תוך',
  no_transaction: 'ללא תנועות תוך',
  debt_threshold: 'חוב מעל',
  supplier_no_payment: 'לא שולם תוך',
  supplier_no_purchase: 'ללא רכישות תוך',
  supplier_balance_above: 'יתרה מעל',
  product_low_stock: 'מלאי מתחת ל',
  product_out_of_stock: 'אזל מהמלאי',
};

const ENTITY_LABELS = {
  client: 'לקוח',
  supplier: 'ספק',
  product: 'מוצר',
};

const CHANNEL_LABELS = {
  in_app: 'באפליקציה',
  email: 'אימייל',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};

const formatRuleDescription = (rule) => {
  const entity = ENTITY_LABELS[rule.EntityType] || rule.EntityType;
  const condition = RULE_TYPE_LABELS[rule.RuleType] || rule.RuleType;
  const timeRules = ['no_payment', 'no_charge', 'no_transaction', 'supplier_no_payment', 'supplier_no_purchase'];
  const amountRules = ['debt_threshold', 'supplier_balance_above'];
  const productAmountRules = ['product_low_stock'];
  const noParamRules = ['product_out_of_stock'];

  let param = '';
  if (timeRules.includes(rule.RuleType)) {
    param = `${rule.ThresholdMonths} חודשים`;
  } else if (amountRules.includes(rule.RuleType)) {
    param = `₪${Number(rule.ThresholdAmount).toLocaleString()}`;
  } else if (productAmountRules.includes(rule.RuleType)) {
    param = `${Number(rule.ThresholdAmount).toLocaleString()} יחידות`;
  } else if (noParamRules.includes(rule.RuleType)) {
    param = '';
  }

  let debtPart = '';
  if (rule.RequireDebt && rule.EntityType === 'client' && rule.RuleType !== 'debt_threshold') {
    debtPart = ` + חוב מעל ₪${Number(rule.DebtThreshold).toLocaleString()}`;
  }

  return `${entity} ${condition} ${param}${debtPart}`;
};

const NotificationRulesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [error, setError] = useState('');

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['notification-rules'],
    queryFn: getNotificationRules,
    staleTime: 60000,
  });

  const toggleMutation = useMutation({
    mutationFn: (rule) => updateNotificationRule({
      NotificationRuleID: rule.NotificationRuleID,
      IsActive: !rule.IsActive,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-rules'] }),
    onError: (err) => setError(err?.response?.data?.error || 'שגיאה'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNotificationRule,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-rules'] }),
    onError: (err) => setError(err?.response?.data?.error || 'שגיאה'),
  });

  const handleAdd = useCallback(() => {
    setEditingRule(null);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((rule) => {
    setEditingRule(rule);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback((ruleId) => {
    deleteMutation.mutate(ruleId);
  }, [deleteMutation]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingRule(null);
  }, []);

  const handleDialogSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notification-rules'] });
    setDialogOpen(false);
    setEditingRule(null);
  }, [queryClient]);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
          <IconButton onClick={() => navigate('/notifications')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            כללי התראות
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{ textTransform: 'none', direction: 'ltr' }}
        >
          כלל חדש
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : rules.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsActiveIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              אין כללי התראות
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              צור כלל חדש כדי לקבל התראות אוטומטיות
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={{ textTransform: 'none', direction: 'ltr' }}>
              כלל חדש
            </Button>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {rules.map((rule) => (
              <ListItem
                key={rule.NotificationRuleID}
                sx={{
                  py: 2,
                  px: 2.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  opacity: rule.IsActive ? 1 : 0.6,
                }}
              >
                <Switch
                  checked={rule.IsActive}
                  onChange={() => toggleMutation.mutate(rule)}
                  size="small"
                  color="primary"
                />
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, justifyContent: 'flex-start' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {rule.Name}
                    </Typography>
                    {rule.IsSystem && (
                      <Chip label="ברירת מחדל" size="small" variant="outlined" color="default" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatRuleDescription(rule)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Chip
                      label={ENTITY_LABELS[rule.EntityType]}
                      size="small"
                      color={rule.EntityType === 'client' ? 'primary' : rule.EntityType === 'product' ? 'warning' : 'secondary'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                    <Chip
                      label={CHANNEL_LABELS[rule.NotifyChannel]}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="ערוך">
                    <IconButton size="small" onClick={() => handleEdit(rule)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="מחק">
                    <IconButton size="small" onClick={() => handleDelete(rule.NotificationRuleID)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <NotificationRuleDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSaved={handleDialogSaved}
        editingRule={editingRule}
      />
    </Box>
  );
};

export default NotificationRulesPage;
